// ./src/MapPinPicker/MapPinPicker.tsx
//
// A PIN ON A MAP, with its matching address (owner, 2026-10-23: "use my location … is not always feasible as if I use a
// computer it just gives me some random location … we need … to find an address and drop a pin on a map … show
// coordinates, and maybe matching address … it can be an address or it can be a pin on a map — a lost & found in a park
// … This component could be reused in other places as well, and maybe even on the event website").
//
// Controlled: `value` is { latitude, longitude, address }. Three ways to set it — SEARCH a place or an address (the pin
// goes there, the address is the place's), CLICK the map or DRAG the pin (the address is looked up for that spot), or
// USE MY LOCATION (where this device is — right when standing there, wrong from an office, which is why it is not the
// only way). The address stays editable: "by the fountain, north end of the park" is a fine address for a pin.
//
// ⓘ Presentational over the shared Maps stack (GoogleMapsContext + PlaceAutocompleteInput + useGoogleMapsAutocomplete),
//   so it runs wherever a GoogleMapsProvider is mounted — the dashboard and the event website. The drawer around it is
//   the host's (an AppDrawer in the dashboard, PublicSideDrawer on the site).
// ⓘ It replaced the volunteer slots' CoordinatePickerModal (owner, 2026-10-23): a coverage circle (`radiusMeters`) and an
//   address that is only SHOWN (`addressMode`) are what that one needed.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Button, Group, Loader, Stack, Text, TextInput, Tooltip } from '@mantine/core';
import { useClipboard } from '@mantine/hooks';

import { AppIcon } from '../AppIcon';
import { useGoogleMapsContext } from '../GoogleMapsContext';
import { useGoogleMapsAutocomplete } from '../hooks';
import { PlaceAutocompleteInput } from '../PlaceAutocompleteInput';

export interface MapPin {
  latitude: number | null;
  longitude: number | null;
  /** What the pin is — the place's address, the address looked up for the spot, or words of the host's own. */
  address: string;
  /**
   * How far off the pin may be, in metres — set only by "Use my location" (the device's own estimate); a pin placed by a
   * search, a click or a drag is null (it is where it was put). Absent = not tracked by this caller.
   */
  accuracyM?: number | null;
}

export const EMPTY_MAP_PIN: MapPin = { latitude: null, longitude: null, address: '' };

export interface MapPinPickerProps {
  value: MapPin;
  onChange: (value: MapPin) => void;
  /** Where the map opens when there is no pin yet (the venue, another point of the event…). */
  center?: { latitude: number; longitude: number } | null;
  /** The map's height. */
  height?: number | string;
  disabled?: boolean;
  /** Under the address box. */
  addressDescription?: string;
  /**
   * The address: `edit` — a box the host may reword (stored with the pin); `show` — the matching address as a hint only
   * (where the record keeps no address, e.g. a volunteer slot's coordinates); `hide`.
   */
  addressMode?: 'edit' | 'show' | 'hide';
  /** A circle around the pin, in metres (a coverage radius); none when absent or 0. */
  radiusMeters?: number | null;
  /**
   * The search's list of places opens in a portal (default) — set false inside a surface the list must stay within to
   * keep its colours (the event website's themed drawer: a portal leaves the event theme).
   */
  searchWithinPortal?: boolean;
}

const WORLD = { lat: 40, lng: -100 };
const WORLD_ZOOM = 3;
const AREA_ZOOM = 14;
const PIN_ZOOM = 17;
const MAP_ID = 'map-pin-picker';
const SEARCH_TYPES = ['establishment', 'geocode'];

const hasPin = (v: MapPin): boolean => v.latitude != null && v.longitude != null;

/** One line for a place a search found: its name, then its street and city (the name alone when it IS the street). */
const placeLine = (name: string, a: { address1?: string | null; city?: string | null; region?: string | null } | null | undefined): string => {
  const parts = [name, a?.address1, a?.city, a?.region].map((s) => (s ?? '').trim()).filter(Boolean);

  return Array.from(new Set(parts)).join(', ');
};

export const MapPinPicker: React.FC<MapPinPickerProps> = ({
  value,
  onChange,
  center = null,
  height = 360,
  disabled = false,
  addressDescription,
  addressMode = 'edit',
  radiusMeters = null,
  searchWithinPortal = true,
}) => {
  const { loadLibraries } = useGoogleMapsContext();
  const { reverseGeocode } = useGoogleMapsAutocomplete();
  // ⚠ The map is made ONCE per mount: `loadLibraries` changes identity the first time Maps loads on a page, and an
  //   effect keyed on it re-made the map after the pin had been placed — leaving a map with no pin (2026-10-23).
  const loadLibrariesRef = useRef(loadLibraries);

  loadLibrariesRef.current = loadLibraries;
  const clipboard = useClipboard({ timeout: 1500 });

  const [search, setSearch] = useState('');
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const markerLibRef = useRef<google.maps.MarkerLibrary | null>(null);
  const mapsLibRef = useRef<typeof google.maps | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  // The latest value and callback, for listeners bound once (the map's click, the pin's drag).
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const lookupSeq = useRef(0);

  valueRef.current = value;
  onChangeRef.current = onChange;

  // Words the host typed into the address box in THIS session: moving the pin keeps them, a look-up never overwrites
  // them. Anything else in the box (a search's place, a looked-up address, a stored one) belongs to where the pin WAS.
  const typedRef = useRef(false);
  // The spot last looked up — the look-up below runs once per spot, "nothing found" included.
  const lookedUpRef = useRef<string | null>(null);

  /** Look up the address of a spot; applied only while the pin is still there and the host has not typed their own. */
  const lookUp = useCallback(
    async (lat: number, lng: number) => {
      const seq = ++lookupSeq.current;

      lookedUpRef.current = `${lat},${lng}`;
      setLookingUp(true);

      const found = await reverseGeocode({ latitude: lat, longitude: lng }).catch(() => null);

      if (seq !== lookupSeq.current) {
        return;
      }

      setLookingUp(false);

      const v = valueRef.current;

      if (v.latitude === lat && v.longitude === lng && !typedRef.current) {
        onChangeRef.current({ ...v, address: found?.displayName ?? '' });
      }
    },
    [reverseGeocode],
  );

  /**
   * A pin at a spot: its address is the one given (a search's place), else looked up for it (the latest look-up wins).
   * ⚠ Until the look-up answers, the old spot's address is NOT kept (saving then stored one place's address with
   *   another's pin) — only words the host typed are. `accuracyM` is the device's estimate when the spot came from
   *   "Use my location"; every other way of placing the pin has none.
   */
  const pinAt = useCallback(
    async (lat: number, lng: number, address?: string, accuracyM: number | null = null) => {
      if (address !== undefined) {
        typedRef.current = false;
        lookedUpRef.current = `${lat},${lng}`;
        ++lookupSeq.current;
        setLookingUp(false);
        onChangeRef.current({ latitude: lat, longitude: lng, address, accuracyM });

        return;
      }

      onChangeRef.current({ latitude: lat, longitude: lng, address: typedRef.current ? valueRef.current.address : '', accuracyM });
      await lookUp(lat, lng);
    },
    [lookUp],
  );

  // ── 'show' over a pin that came without its address (a stored pin: the record keeps none): look it up, once per
  //    spot. ⓘ Reacts to the VALUE, not to the mount — a drawer hands its pin over a render after the picker mounts ──
  useEffect(() => {
    if (addressMode !== 'show' || !hasPin(value) || value.address.trim() || lookedUpRef.current === `${value.latitude},${value.longitude}`) {
      return;
    }

    void lookUp(value.latitude as number, value.longitude as number);
  }, [addressMode, value, lookUp]);

  // ── The map, once ──
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        const libs = await loadLibrariesRef.current(['maps', 'marker']);

        if (cancelled || !containerRef.current) {
          return;
        }

        markerLibRef.current = libs.marker;
        mapsLibRef.current = libs.maps;

        const v = valueRef.current;
        const start = hasPin(v) ? { lat: v.latitude as number, lng: v.longitude as number } : center ? { lat: center.latitude, lng: center.longitude } : WORLD;
        const map = new libs.maps.Map(containerRef.current, {
          center: start,
          zoom: hasPin(v) ? PIN_ZOOM : center ? AREA_ZOOM : WORLD_ZOOM,
          mapId: MAP_ID,
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        });

        map.addListener('click', (e: google.maps.MapMouseEvent) => {
          if (e.latLng && !disabled) {
            void pinAt(e.latLng.lat(), e.latLng.lng());
          }
        });

        mapRef.current = map;
        setMapReady(true);
      } catch {
        if (!cancelled) {
          setMapError('The map could not be loaded. You can still type the address.');
        }
      }
    };

    void init();

    return () => {
      cancelled = true;

      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }

      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }

      mapRef.current = null;
      setMapReady(false);
    };
    // The map is made once per mount; later values move the pin (below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── The pin follows the value ──
  useEffect(() => {
    if (!mapReady || !mapRef.current || !markerLibRef.current) {
      return;
    }

    if (!hasPin(value)) {
      if (markerRef.current) {
        markerRef.current.map = null;
        markerRef.current = null;
      }

      return;
    }

    const position = { lat: value.latitude as number, lng: value.longitude as number };

    if (markerRef.current) {
      markerRef.current.position = position;

      return;
    }

    // A pin out of view is brought into it — a drawer hands its pin over a render after the map was made, so the map
    // may have opened on the world.
    const bounds = mapRef.current.getBounds();

    if (!bounds || !bounds.contains(position)) {
      mapRef.current.setCenter(position);

      if ((mapRef.current.getZoom() ?? 0) < PIN_ZOOM) {
        mapRef.current.setZoom(PIN_ZOOM);
      }
    }

    const marker = new markerLibRef.current.AdvancedMarkerElement({ position, map: mapRef.current, gmpDraggable: !disabled, title: 'Drag to move' });

    marker.addListener('dragend', () => {
      const p = marker.position as google.maps.LatLngLiteral | google.maps.LatLng | null;

      if (!p) {
        return;
      }

      const lat = typeof (p as google.maps.LatLng).lat === 'function' ? (p as google.maps.LatLng).lat() : (p as google.maps.LatLngLiteral).lat;
      const lng = typeof (p as google.maps.LatLng).lng === 'function' ? (p as google.maps.LatLng).lng() : (p as google.maps.LatLngLiteral).lng;

      void pinAt(lat, lng);
    });

    markerRef.current = marker;
  }, [mapReady, value, disabled, pinAt]);

  // ── The coverage circle follows the pin and the radius ──
  useEffect(() => {
    if (!mapReady || !mapRef.current || !mapsLibRef.current) {
      return;
    }

    const radius = Number(radiusMeters) || 0;

    if (!hasPin(value) || radius <= 0) {
      circleRef.current?.setMap(null);
      circleRef.current = null;

      return;
    }

    const center = { lat: value.latitude as number, lng: value.longitude as number };

    if (circleRef.current) {
      circleRef.current.setCenter(center);
      circleRef.current.setRadius(radius);

      return;
    }

    circleRef.current = new mapsLibRef.current.Circle({
      center,
      radius,
      map: mapRef.current,
      editable: false,
      clickable: false,
      strokeColor: '#ae3ec9',
      strokeOpacity: 0.9,
      strokeWeight: 2,
      fillColor: '#ae3ec9',
      fillOpacity: 0.12,
    });
  }, [mapReady, value, radiusMeters]);

  const flyTo = (lat: number, lng: number) => {
    mapRef.current?.panTo({ lat, lng });
    mapRef.current?.setZoom(PIN_ZOOM);
  };

  const useMyLocation = () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocateError('This browser cannot tell where it is.');

      return;
    }

    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        flyTo(pos.coords.latitude, pos.coords.longitude);
        void pinAt(pos.coords.latitude, pos.coords.longitude, undefined, Number.isFinite(pos.coords.accuracy) ? Math.round(pos.coords.accuracy) : null);
      },
      (err) => {
        setLocating(false);
        setLocateError(err.code === err.PERMISSION_DENIED ? 'Location is blocked for this site — search or click the map instead.' : 'Your location could not be read right now.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const coords = hasPin(value) ? `${(value.latitude as number).toFixed(6)}, ${(value.longitude as number).toFixed(6)}` : null;

  return (
    <Stack gap="sm">
      <PlaceAutocompleteInput
        label="Find a place or an address"
        placeholder="e.g. Prince's Island Park, or 1 Main St"
        value={search}
        placeTypes={SEARCH_TYPES}
        withinPortal={searchWithinPortal}
        disabled={disabled}
        onChange={(name, place) => {
          setSearch(name);

          const lat = place?.address?.latitude;
          const lng = place?.address?.longitude;

          if (typeof lat === 'number' && typeof lng === 'number') {
            flyTo(lat, lng);
            void pinAt(lat, lng, placeLine(place?.name ?? name, place?.address));
          }
        }}
      />

      <Text size="md" c="dimmed">
        Or click the map to drop the pin, and drag it to fine-tune.
      </Text>

      <Box pos="relative" style={{ height, borderRadius: 'var(--mantine-radius-md)', overflow: 'hidden', border: '1px solid var(--mantine-color-default-border)' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%' }} data-map-pin-picker />
        {!mapReady && !mapError ? (
          <Group justify="center" align="center" pos="absolute" inset={0}>
            <Loader size="sm" />
          </Group>
        ) : null}
        {mapError ? (
          <Group justify="center" align="center" pos="absolute" inset={0} p="md">
            <Text size="md" c="dimmed" ta="center">
              {mapError}
            </Text>
          </Group>
        ) : null}
      </Box>

      <Group gap="sm" wrap="wrap" align="center">
        <Button variant="light" size="md" onClick={useMyLocation} loading={locating} disabled={disabled} leftSection={<AppIcon iconName="CurrentLocation" size={16} />}>
          Use my location
        </Button>
        {coords ? (
          <Group gap={4} wrap="nowrap">
            <Text size="md" ff="monospace">
              {coords}
            </Text>
            <Tooltip label={clipboard.copied ? 'Copied' : 'Copy'} withArrow>
              <ActionIcon variant="subtle" color="gray" size="lg" aria-label="Copy the coordinates" onClick={() => clipboard.copy(coords)}>
                <AppIcon iconName={clipboard.copied ? 'Check' : 'Copy'} size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Remove the pin" withArrow>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="lg"
                aria-label="Remove the pin"
                disabled={disabled}
                onClick={() => {
                  typedRef.current = false;
                  onChange(EMPTY_MAP_PIN);
                }}
              >
                <AppIcon iconName="X" size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ) : (
          <Text size="md" c="dimmed">
            No pin yet
          </Text>
        )}
      </Group>
      {locateError ? (
        <Text size="md" c="red">
          {locateError}
        </Text>
      ) : null}

      {addressMode === 'edit' ? (
        <TextInput
          size="md"
          label="Address"
          description={addressDescription ?? 'Filled in from the pin — change it to say it your way (e.g. "North end of the park, by the fountain").'}
          value={value.address}
          onChange={(e) => {
            const address = e.currentTarget.value;

            typedRef.current = true;
            onChange({ ...value, address });
          }}
          rightSection={lookingUp ? <Loader size="xs" /> : null}
          disabled={disabled}
          maxLength={256}
        />
      ) : addressMode === 'show' && hasPin(value) ? (
        <Group gap="xs" wrap="nowrap" align="flex-start">
          <AppIcon iconName="MapPin" size={16} style={{ marginTop: 4, flexShrink: 0 }} />
          <Text size="md" c="dimmed">
            {lookingUp ? 'Looking up the address…' : value.address ? `Near ${value.address}` : 'No address found for this spot'}
          </Text>
        </Group>
      ) : null}
    </Stack>
  );
};
